import { school } from '../constants/school.js';

/*
 * The canonical origin. Overridden at build time so a staging deploy does not
 * publish canonical URLs and Open Graph tags pointing at production.
 */
export const SITE_URL = (
  import.meta.env?.VITE_SITE_URL ?? 'https://toddlersacademy.sch'
).replace(/\/$/, '');

/**
 * Per-route metadata, in one place because two very different consumers read
 * it: <Seo> sets it on client-side navigation, and the prerender script writes
 * it into the static HTML at build time. Splitting them would mean a title that
 * changes when JavaScript loads.
 *
 * `noindex` keeps the staff area out of search results.
 */
export const ROUTE_META = {
  '/': {
    title: "Toddler's Academy — every child starts with one step",
    description:
      "Toddler's Academy is a public school in Lagos where 18 children share one classroom and one teacher who knows every single name. Ages 2–16, no fees, ever.",
  },
  '/about': {
    title: "About us — Toddler's Academy",
    description:
      'Open since 1998 on Ogundele Way: 412 children, 31 teachers, and a class-size ceiling of eighteen we have never once raised.',
  },
  '/academics': {
    title: "Academics — Toddler's Academy",
    description:
      'The Toddler Room, Primary and Secondary — what is taught at each stage, class by class, plus a real Tuesday in Primary 3.',
  },
  '/facilities': {
    title: "Facilities — Toddler's Academy",
    description:
      'A 9,400-book library, two science labs, a workshop, a fenced toddler garden, a full-size pitch, an on-site kitchen and a clinic.',
  },
  '/news': {
    title: "News & events — Toddler's Academy",
    description:
      'What happened at school this week, and the open mornings, offer dates and term dates coming up.',
  },
  '/gallery': {
    title: "Gallery — Toddler's Academy",
    description:
      'A normal week at school, photographed by teachers: classrooms, the Toddler Room, sport, events and the campus.',
  },
  '/achievements': {
    title: "Achievements — Toddler's Academy",
    description:
      'A 100% Grade 10 pass rate three years running, 82% of leavers at their first-choice school, and the honours behind those figures.',
  },
  '/contact': {
    title: "Contact us — Toddler's Academy",
    description:
      'Call 0800 114 2255 between 8 and 5, send an enquiry, or walk in on a Tuesday at 10:00. 14 Ogundele Way, Ikeja, Lagos.',
  },
  '/privacy': {
    title: "Privacy notice — Toddler's Academy",
    description:
      'What we collect when you send an admissions enquiry, how long we keep it, who can see it, and how to ask us to delete it.',
  },
  '/staff/login': {
    title: "Staff sign in — Toddler's Academy",
    description: 'Sign in to the admissions dashboard.',
    noindex: true,
  },
  '/staff': {
    title: "Admissions dashboard — Toddler's Academy",
    description: 'Triage and track admissions enquiries.',
    noindex: true,
  },
  '/404': {
    title: "Page not found — Toddler's Academy",
    description: 'That page does not exist.',
    noindex: true,
  },
};

/** Routes worth writing static HTML for: public, indexable, no data needed. */
export const PRERENDER_ROUTES = [
  '/',
  '/about',
  '/academics',
  '/facilities',
  '/news',
  '/gallery',
  '/achievements',
  '/contact',
  '/privacy',
];

export function metaFor(pathname) {
  return ROUTE_META[pathname] ?? ROUTE_META['/404'];
}

/*
 * Schema.org for a school. This is what puts the address, phone number and
 * opening hours in a search result rather than just a blue link, and it is the
 * one piece of SEO with a directly visible payoff for a local institution.
 */
export function schoolJsonLd() {
  const [street, area, city] = school.address.split(',').map((part) => part.trim());

  return {
    '@context': 'https://schema.org',
    '@type': 'School',
    name: school.name,
    description: ROUTE_META['/'].description,
    url: SITE_URL,
    telephone: school.phone,
    email: school.email,
    slogan: school.tagline,
    address: {
      '@type': 'PostalAddress',
      streetAddress: street,
      addressLocality: area,
      addressRegion: city,
      addressCountry: 'NG',
    },
    openingHoursSpecification: {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '07:30',
      closes: '17:00',
    },
    isAccessibleForFree: true,
    publicAccess: true,
  };
}
